import * as React from 'react';
import type { NodeTemplate } from '@features/flows/palette/registry';
import { HeadlessSchemaConfigForm, buildJsonSchema, type SchemaConfigMeta } from '@shared/forms/HeadlessSchemaConfigForm';
import { shadcnAdapter } from '@shared/ui/adapters';
import type { NodeBindings, BindingSpec } from '@features/flows/utils/types';
import { scanVariablesInObject } from '@features/flows/lib/liquid/runtime';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Node } from '@xyflow/react';
import { deepMerge } from '@features/flows/utils/deep-merge';
import { uiHintsToRjsf, type UiFieldSpec, type UiFormSpec } from '@shared/forms/ui-hints';

type Props = {
  label: string;
  template?: NodeTemplate;
  value: any | undefined;
  onChange: (next: any) => void;
  disabled?: boolean;
  /** bindings live on node.data; they do not change the form value */
  bindings?: NodeBindings;
  onTogglePin?: (field: string) => void;
  onChangeBinding?: (field: string, spec: Partial<BindingSpec>) => void;
  /** optional: to read per-node ui overrides at node.data.ui */
  node?: Pick<Node, 'id' | 'data'>;
};

function NodeAttrsFormComponent({ label, template, value, onChange, disabled, bindings, onTogglePin, onChangeBinding, node }: Props) {
  // Compose meta: prefer template.attrsSchema; fallback to empty object schema
  const meta = React.useMemo<SchemaConfigMeta>(() => {
    const schema = (template?.attrsSchema as any) ?? { type: 'object', properties: {}, additionalProperties: true };
    return {
      id: `node-attrs:${template?.key ?? 'default'}`,
      title: `Attributes — ${label}`,
      description: template?.description,
      // Prefer backend-provided handler version from the palette; fallback to semantic default
      version: (template?.version as any) ?? '1.0.0',
      schema,
    };
  }, [template?.key, template?.description, template?.version, label]);

  // Best-effort list of schema field keys for the forwarding UI.
  const fieldKeys = React.useMemo<string[]>(() => {
    try {
      const js = buildJsonSchema(meta);
      if ((js as any)?.properties) return Object.keys((js as any).properties as Record<string, unknown>);
    } catch {}
    return Object.keys((value ?? {}) as Record<string, unknown>);
  }, [meta, JSON.stringify(value ?? {})]);

  const discovered = React.useMemo(() => Array.from(scanVariablesInObject(value ?? {})).sort(), [JSON.stringify(value ?? {})]);

  // Build UI schema & patched JSON schema from template hints (+ node overrides + pinned disabling)
  const { finalMeta, uiOverrides } = React.useMemo(() => {
    const tAttrsUi = (template?.attrsUi as Record<string, UiFieldSpec> | undefined) ?? undefined;
    const tFormUi = (template?.formUi as UiFormSpec | undefined) ?? undefined;
    const nUi = (node?.data as any)?.ui || {};
    const nAttrsUi = (nUi?.attrs as Record<string, UiFieldSpec> | undefined) ?? undefined;
    const nFormUi = (nUi?.form as UiFormSpec | undefined) ?? undefined;
    // Merge with precedence: node overrides > template
    const mergedAttrsUi = deepMerge(tAttrsUi ?? {}, nAttrsUi ?? {});
    const mergedFormUi = nFormUi ?? tFormUi;
    const { uiSchema, patchedSchema } = uiHintsToRjsf(meta.schema as any, mergedAttrsUi, mergedFormUi);
    // Disable pinned fields on top
    const pinned = new Set(fieldKeys.filter((k) => bindings?.[k]?.pin && bindings?.[k]?.precedence === 'pinOnly'));
    const disablePinned = Object.fromEntries(Array.from(pinned).map((k) => [k, { 'ui:disabled': true }]));
    const ui = deepMerge(uiSchema as any, disablePinned as any);
    return {
      finalMeta: { ...meta, schema: patchedSchema } as SchemaConfigMeta,
      uiOverrides: ui as any,
    };
  }, [meta, template?.attrsUi, template?.formUi, node, fieldKeys.join('|'), JSON.stringify(bindings ?? {})]);

  const adapter = React.useMemo(
    () => ({
      ...shadcnAdapter,
      formOverrides: {
        ...(shadcnAdapter.formOverrides ?? {}),
        uiSchema: uiOverrides,
      },
    }),
    [uiOverrides],
  );

  const handleTogglePin = React.useCallback((field: string) => {
    onTogglePin?.(field);
  }, [onTogglePin]);

  const handleChangeBinding = React.useCallback((field: string, spec: Partial<BindingSpec>) => {
    onChangeBinding?.(field, spec);
  }, [onChangeBinding]);

  return (
    <div className="grid gap-3">
      <HeadlessSchemaConfigForm
        meta={finalMeta}
        value={value}
        onChange={onChange}
        adapter={adapter}
        disabled={disabled}
        enableArrayControls
      />
      <Separator />
      <section className="space-y-2">
        <div className="text-xs text-muted-foreground">Forwarding &amp; Bindings</div>
        <div className="grid gap-1">
          {fieldKeys.map((k) => {
            return (
              <BindingRow
                key={k}
                field={k}
                spec={bindings?.[k]}
                onTogglePin={handleTogglePin}
                onChangeBinding={handleChangeBinding}
              />
            );
          })}
        </div>
      </section>
      {discovered.length > 0 && (
        <section className="space-y-1">
          <div className="text-xs text-muted-foreground">Discovered inputs (from templates)</div>
          <div className="flex flex-wrap gap-1">
            {discovered.map((d) => (
              <span key={d} className="px-1.5 py-0.5 text-xs rounded border bg-muted/50">{d}</span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

NodeAttrsFormComponent.displayName = 'NodeAttrsForm';

export default React.memo(NodeAttrsFormComponent);

type BindingRowProps = {
  field: string;
  spec: BindingSpec | undefined;
  onTogglePin?: (field: string) => void;
  onChangeBinding?: (field: string, spec: Partial<BindingSpec>) => void;
};

const BindingRow = React.memo(function BindingRow({ field, spec, onTogglePin, onChangeBinding }: BindingRowProps) {
  const pinned = !!spec?.pin && spec?.precedence === 'pinOnly';
  const isNonExposable = spec?.exposable === false;

  const handleToggle = React.useCallback(() => {
    if (isNonExposable) return;
    onTogglePin?.(field);
  }, [field, isNonExposable, onTogglePin]);

  const handleCombineChange = React.useCallback((value: string) => {
    onChangeBinding?.(field, { combine: value as BindingSpec['combine'] });
  }, [field, onChangeBinding]);

  return (
    <div className="flex items-center gap-2">
      <div className="grow truncate">
        <span className="text-sm font-medium">{field}</span>
        {spec?.sensitive ? <span className="ml-2 text-[10px] uppercase tracking-wide text-rose-500">sensitive</span> : null}
      </div>
      <Button
        size="sm"
        variant={pinned ? 'secondary' : 'outline'}
        onClick={handleToggle}
        disabled={isNonExposable}
        title={isNonExposable ? 'This field is not exposable as a pin (secret)' : undefined}
      >
        {pinned ? 'Unpin' : 'Pin'}
      </Button>
      {/* precedence is defined by node creator; hide from user */}
      <Select
        value={spec?.combine ?? 'replace'}
        onValueChange={handleCombineChange}
      >
        <SelectTrigger className="w-[112px]"><SelectValue placeholder="combine" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="replace">replace</SelectItem>
          <SelectItem value="merge">merge</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
});

BindingRow.displayName = 'BindingRow';
